import { useId, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { useReducedMotion } from '@/lib/useReducedMotion'

export type ProjectVisual = 'creative' | 'playstat' | 'pm-kit' | 'ontology' | 'scrum' | 'marketing' | 'content' | 'skills'

function Node({ x, y, width, label, accent = false }: { x: number; y: number; width: number; label: string; accent?: boolean }) {
  return (
    <g>
      <rect x={x} y={y} width={width} height="32" rx="5" fill={accent ? '#261815' : '#131a16'} stroke={accent ? '#f34e3f' : '#3b443d'} />
      <text x={x + width / 2} y={y + 20} textAnchor="middle" fill="#dfdfc1" fontSize="11">{label}</text>
    </g>
  )
}

function Paper({ x, y, children }: { x: number; y: number; children?: ReactNode }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width="72" height="82" rx="5" fill="#131a16" stroke="#3b443d" />
      <path d="M14 18H42 M14 28H56 M14 38H50" stroke="#69736a" strokeWidth="2" strokeLinecap="round" />
      {children}
    </g>
  )
}

const descriptions: Record<ProjectVisual, string> = {
  creative: 'A brand brief feeds image and video drafts for human approval. Feedback returns to improve the prompts.',
  playstat: 'Multiple analysis agents feed a synthesis and a market brief for human review.',
  'pm-kit': 'Specs, decisions, and status form a shared workspace for a coding agent.',
  ontology: 'A transcript connects through an ontology to people, topics, decisions, and actions.',
  scrum: 'A backlog board with to-do, doing, and done columns connects to a Hermes status update.',
  marketing: 'An audience query leads to an audience segment and an HTML email. Feedback returns to the workflow.',
  content: 'A content queue feeds a Threads post. The channel has 2.7k followers.',
  skills: 'Coding, planning, and creative skills are available to Claude Code.',
}

export function ProjectDiagram({ visual, animate = false }: { visual: ProjectVisual; animate?: boolean }) {
  const id = useId()
  const reducedMotion = useReducedMotion()
  const arrow = `url(#${id}-arrow)`
  const featured = visual === 'creative' || visual === 'playstat'
  const trace = { hidden: { pathLength: 0 }, visible: { pathLength: 1, transition: { duration: reducedMotion ? 0 : 0.7 } } }

  return (
    <figure className="mt-5 overflow-hidden rounded-lg border border-vibe-border bg-[#0b0d0b]">
      <motion.svg initial={animate && !reducedMotion ? 'hidden' : false} whileInView="visible" viewport={{ once: true, amount: 0.3 }} viewBox={featured ? '0 0 440 210' : '0 0 300 170'} role="img" aria-labelledby={`${id}-title`} className="block w-full font-mono">
        <title id={`${id}-title`}>{descriptions[visual]}</title>
        <defs>
          <marker id={`${id}-arrow`} viewBox="0 0 6 6" refX="5" refY="3" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0 0L6 3L0 6" fill="none" stroke="#f34e3f" />
          </marker>
        </defs>
        {visual === 'creative' && (
          <>
            <Paper x={25} y={40}><text x="36" y="66" textAnchor="middle" fill="#dfdfc1" fontSize="11">Brief</text></Paper>
            <motion.path variants={trace} d="M100 81H153" stroke="#f34e3f" fill="none" markerEnd={arrow} />
            <motion.path variants={trace} d="M282 81H327" stroke="#f34e3f" fill="none" markerEnd={arrow} />
            <rect x="161" y="30" width="92" height="72" rx="5" fill="#131a16" stroke="#3b443d" />
            <circle cx="181" cy="49" r="5" fill="#69736a" />
            <path d="M170 86L195 61L207 72L223 53L245 86Z" fill="#29372d" stroke="#69736a" />
            <rect x="201" y="66" width="76" height="53" rx="5" fill="#1b211a" stroke="#69736a" />
            <path d="M231 81L248 92L231 103Z" fill="#dfdfc1" />
            <text x="218" y="143" textAnchor="middle" fill="#9ca49c" fontSize="11">Image / video drafts</text>
            <circle cx="366" cy="55" r="12" fill="none" stroke="#dfdfc1" />
            <path d="M345 99V90C345 69 387 69 387 90V99Z" fill="#131a16" stroke="#dfdfc1" />
            <text x="366" y="120" textAnchor="middle" fill="#dfdfc1" fontSize="11">Human</text>
            <text x="366" y="135" textAnchor="middle" fill="#dfdfc1" fontSize="11">approval</text>
            <motion.path variants={trace} d="M366 151V174H62V130" fill="none" stroke="#f34e3f" strokeOpacity="0.7" markerEnd={arrow} />
            <rect x="130" y="164" width="170" height="20" fill="#0b0d0b" />
            <text x="215" y="178" textAnchor="middle" fill="#f34e3f" fontSize="10">Feedback improves prompts</text>
          </>
        )}
        {visual === 'playstat' && (
          <>
            {[30, 85, 140].map((y, i) => <Node key={y} x={20} y={y} width={107} label={`Agent 0${i + 1}`} />)}
            <motion.path variants={trace} d="M127 46H149V156H127 M127 101H149" fill="none" stroke="#f34e3f" />
            <motion.path variants={trace} d="M149 101H172" fill="none" stroke="#f34e3f" markerEnd={arrow} />
            <Node x={178} y={85} width={90} label="Synthesis" accent />
            <motion.path variants={trace} d="M269 101H296" fill="none" stroke="#f34e3f" markerEnd={arrow} />
            <Paper x={304} y={42}><text x="36" y="58" textAnchor="middle" fill="#dfdfc1" fontSize="11">Market</text><text x="36" y="72" textAnchor="middle" fill="#dfdfc1" fontSize="11">brief</text></Paper>
            <path d="M340 126V148" stroke="#f34e3f" markerEnd={arrow} />
            <Node x={288} y={155} width={104} label="Human review" />
            <text x="222" y="141" textAnchor="middle" fill="#9ca49c" fontSize="10">Combine analysis</text>
          </>
        )}
        {(visual === 'pm-kit' || visual === 'skills') && (
          <>
            <rect x="16" y="20" width="143" height="130" rx="5" fill="#131a16" stroke="#3b443d" />
            <text x="29" y="42" fill="#dfdfc1" fontSize="12">{visual === 'pm-kit' ? 'pm-kit/' : 'cc-skills/'}</text>
            <path d="M32 55V128 M32 68H44 M32 96H44 M32 124H44" stroke="#69736a" fill="none" />
            {(visual === 'pm-kit' ? ['specs/', 'decisions/', 'status/'] : ['coding/', 'planning/', 'creative/']).map((label, i) => (
              <text key={label} x="51" y={72 + i * 28} fill="#9ca49c" fontSize="12">{label}</text>
            ))}
            <path d="M160 85H184" stroke="#f34e3f" markerEnd={arrow} />
            <rect x="191" y="45" width="94" height="83" rx="5" fill="#131a16" stroke="#3b443d" />
            <text x="238" y="78" textAnchor="middle" fill="#f34e3f" fontSize="23">{'</>'}</text>
            <text x="238" y="106" textAnchor="middle" fill="#dfdfc1" fontSize="11">{visual === 'pm-kit' ? 'Coding agent' : 'Claude Code'}</text>
          </>
        )}
        {visual === 'ontology' && (
          <>
            <Paper x={14} y={44}><text x="36" y="65" textAnchor="middle" fill="#dfdfc1" fontSize="10">Transcript</text></Paper>
            <path d="M87 85H107 M166 85H182V32H207 M182 85V138H207 M182 67H207 M182 103H207" stroke="#f34e3f" fill="none" />
            <circle cx="138" cy="85" r="28" fill="#261815" stroke="#f34e3f" />
            <text x="138" y="89" textAnchor="middle" fill="#dfdfc1" fontSize="10">Ontology</text>
            {['People', 'Topics', 'Decisions', 'Actions'].map((label, i) => <Node key={label} x={208} y={16 + i * 35} width={79} label={label} />)}
          </>
        )}
        {visual === 'scrum' && (
          <>
            {['To do', 'Doing', 'Done'].map((label, i) => (
              <g key={label} transform={`translate(${16 + i * 92} 17)`}>
                <rect width="84" height="101" rx="5" fill="#131a16" stroke="#3b443d" />
                <text x="11" y="21" fill="#dfdfc1" fontSize="11">{label}</text>
                {Array.from({ length: 3 - i }, (_, j) => (
                  <g key={j}>
                    <rect x="9" y={33 + j * 20} width="66" height="14" rx="2" fill={i === 1 ? '#43251e' : '#29372d'} />
                    <path d={`M15 ${40 + j * 20}H${i === 2 ? 57 : 65 - j * 9}`} stroke="#9ca49c" />
                  </g>
                ))}
              </g>
            ))}
            <path d="M150 119V135" stroke="#f34e3f" markerEnd={arrow} />
            <circle cx="52" cy="149" r="3" fill="#f34e3f" />
            <text x="64" y="153" fill="#9ca49c" fontSize="11">Hermes · status update</text>
          </>
        )}
        {visual === 'marketing' && (
          <>
            <Node x={13} y={54} width={78} label="Query" />
            <Node x={112} y={54} width={78} label="Audience" />
            <path d="M93 70H106" stroke="#f34e3f" markerEnd={arrow} />
            <path d="M192 70H208" stroke="#f34e3f" markerEnd={arrow} />
            <rect x="216" y="41" width="68" height="64" rx="5" fill="#131a16" stroke="#3b443d" />
            <path d="M226 51H274V78H226Z M226 52L250 68L274 52" fill="none" stroke="#dfdfc1" />
            <text x="250" y="96" textAnchor="middle" fill="#9ca49c" fontSize="10">HTML email</text>
            <path d="M251 111V134H52V94" stroke="#f34e3f" fill="none" markerEnd={arrow} />
            <rect x="104" y="123" width="103" height="20" fill="#0b0d0b" />
            <text x="155" y="138" textAnchor="middle" fill="#f34e3f" fontSize="11">Learn & refine</text>
          </>
        )}
        {visual === 'content' && (
          <>
            {[0, 1, 2].map((i) => <rect key={i} x={21 + i * 6} y={43 - i * 6} width="76" height="82" rx="5" fill="#131a16" stroke="#3b443d" />)}
            <path d="M46 51H92 M46 63H81 M46 75H87" stroke="#69736a" strokeWidth="2" />
            <text x="70" y="97" textAnchor="middle" fill="#dfdfc1" fontSize="11">Queue</text>
            <path d="M115 78H158" stroke="#f34e3f" markerEnd={arrow} />
            <rect x="165" y="30" width="117" height="94" rx="6" fill="#131a16" stroke="#3b443d" />
            <circle cx="184" cy="49" r="6" fill="#dfdfc1" />
            <text x="198" y="53" fill="#dfdfc1" fontSize="11">Threads</text>
            <path d="M179 71H267 M179 83H256 M179 95H262 M179 107H220" stroke="#69736a" strokeWidth="2" />
            <text x="224" y="146" textAnchor="middle" fill="#9ca49c" fontSize="10">2.7k followers</text>
          </>
        )}
      </motion.svg>
      <figcaption className="border-t border-vibe-border bg-vibe-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-secondary">Conceptual flow</figcaption>
    </figure>
  )
}
